import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfService: React.FC = () => {
  return (
    <div className="bg-white">
      {/* Hero section */}
      <div className="relative bg-gradient-to-r from-blue-50 to-gray-50 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl md:text-6xl">
              Terms of Service
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-500">
              Last Updated: April 1, 2025
            </p>
          </div>
        </div>
      </div>

      {/* Content section */}
      <div className="py-16 bg-white overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg prose-blue mx-auto">
            <h2>1. Introduction</h2>
            <p>
              Welcome to Explanatum ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of our website, products, and services (collectively, the "Services").
            </p>
            <p>
              By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Services.
            </p>

            <h2>2. Eligibility</h2>
            <p>
              You must be at least 16 years old to use our Services. By using our Services, you represent and warrant that you meet this eligibility requirement. If you are using the Services on behalf of a company or other legal entity, you represent that you have the authority to bind that entity to these Terms.
            </p>

            <h2>3. Account Registration</h2>
            <p>
              To access certain features of our Services, you may need to register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
            </p>
            <p>
              You are responsible for safeguarding your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
            </p>

            <h2>4. Subscription Services</h2>
            <p>
              4.1 <strong>Subscription Plans</strong>: We offer various subscription plans for our Services. The features and limitations of each plan are described on our website.
            </p>
            <p>
              4.2 <strong>Billing and Payment</strong>: We use Stripe as our payment processor. When you purchase a subscription, you agree to pay all fees applicable to your subscription plan. Fees are non-refundable except as required by law.
            </p>
            <p>
              4.3 <strong>Payment Processing</strong>: When you make a payment, you agree to be bound by Stripe's terms of service, which can be found at <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer">https://stripe.com/legal</a>.
            </p>
            <p>
              4.4 <strong>Free Trial</strong>: New users are eligible for a 7-day free trial. When you sign up for a trial, you will need to provide your payment details. At the end of the trial period, you will be automatically charged for the subscription plan you selected unless you cancel before the trial ends. You will receive email notifications before your trial expires.
            </p>
            <p>
              4.5 <strong>Subscription Term and Renewal</strong>: Your subscription will automatically renew at the end of each subscription period unless you cancel it before the renewal date. You can cancel your subscription at any time through your account settings or by contacting our customer support.
            </p>
            <p>
              4.6 <strong>Price Changes</strong>: We may change the fees for our Services from time to time. Any price changes will apply to your subscription at the next renewal date. We will notify you of any price changes before they take effect.
            </p>

            <h2>5. User Content</h2>
            <p>
              5.1 <strong>Ownership</strong>: You retain ownership of any content you submit, post, or display on or through our Services ("User Content"). By submitting User Content, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, translate, distribute, and display such User Content in connection with providing and promoting our Services.
            </p>
            <p>
              5.2 <strong>Responsibility for User Content</strong>: You are solely responsible for your User Content and the consequences of posting or publishing it. You represent and warrant that you own or have the necessary rights to post your User Content and that it does not violate the rights of any third party.
            </p>

            <h2>6. Intellectual Property Rights</h2>
            <p>
              6.1 <strong>Our Intellectual Property</strong>: Our Services and all content, features, and functionality thereof, including but not limited to text, graphics, logos, icons, images, audio clips, digital downloads, data compilations, software, and the design, selection, and arrangement thereof, are owned by us, our licensors, or other providers and are protected by copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.
            </p>
            <p>
              6.2 <strong>Limited License</strong>: Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, non-sublicensable license to access and use our Services for your personal or internal business purposes.
            </p>
            <p>
              6.3 <strong>Restrictions</strong>: You may not:
            </p>
            <ul>
              <li>Reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, or transmit any of the material on our Services, except as permitted by these Terms</li>
              <li>Use any illustrations, photographs, video or audio sequences, or any graphics separately from the accompanying text</li>
              <li>Delete or alter any copyright, trademark, or other proprietary rights notices from copies of materials from our Services</li>
              <li>Access or use for any commercial purposes any part of our Services or any services or materials available through our Services</li>
            </ul>

            <h2>7. Prohibited Uses</h2>
            <p>
              You may use our Services only for lawful purposes and in accordance with these Terms. You agree not to use our Services:
            </p>
            <ul>
              <li>In any way that violates any applicable law or regulation</li>
              <li>To transmit or procure the sending of any advertising or promotional material, including any "junk mail," "chain letter," "spam," or any other similar solicitation</li>
              <li>To impersonate or attempt to impersonate us, our employees, another user, or any other person or entity</li>
              <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of our Services, or which may harm us or users of our Services</li>
            </ul>

            <h2>8. Termination</h2>
            <p>
              8.1 <strong>Termination by You</strong>: You may terminate your account at any time by following the instructions on our website or by contacting us.
            </p>
            <p>
              8.2 <strong>Termination by Us</strong>: We may terminate or suspend your account and access to our Services at our sole discretion, without notice, for conduct that we believe violates these Terms or is harmful to other users of our Services, us, or third parties, or for any other reason.
            </p>
            <p>
              8.3 <strong>Effect of Termination</strong>: Upon termination, your right to use our Services will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>

            <h2>9. Disclaimers</h2>
            <p>
              9.1 <strong>"As Is" and "As Available"</strong>: Our Services are provided on an "as is" and "as available" basis, without any warranties of any kind, either express or implied. We disclaim all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
            <p>
              9.2 <strong>No Guarantees</strong>: We do not guarantee that our Services will be uninterrupted, secure, or error-free, or that defects will be corrected. We are not responsible for any loss or damage caused by your reliance on information obtained through our Services.
            </p>

            <h2>10. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, in no event will we, our affiliates, or our licensors, service providers, employees, agents, officers, or directors be liable for damages of any kind, under any legal theory, arising out of or in connection with your use or inability to use our Services, including any direct, indirect, special, incidental, consequential, or punitive damages.
            </p>

            <h2>11. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless us, our affiliates, licensors, and service providers, and our and their respective officers, directors, employees, contractors, agents, licensors, suppliers, successors, and assigns from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of our Services.
            </p>

            <h2>12. Governing Law and Jurisdiction</h2>
            <p>
              These Terms and any dispute or claim arising out of or in connection with them or their subject matter or formation (including non-contractual disputes or claims) shall be governed by and construed in accordance with the laws of the United Kingdom. Any legal suit, action, or proceeding arising out of or related to these Terms or our Services shall be instituted exclusively in the courts of the United Kingdom.
            </p>

            <h2>13. Changes to These Terms</h2>
            <p>
              We may revise and update these Terms from time to time at our sole discretion. All changes are effective immediately when we post them. Your continued use of our Services following the posting of revised Terms means that you accept and agree to the changes.
            </p>

            <h2>14. Entire Agreement</h2>
            <p>
              These Terms, our Privacy Policy, and any other agreements expressly incorporated by reference herein constitute the sole and entire agreement between you and us regarding our Services and supersede all prior and contemporaneous understandings, agreements, representations, and warranties, both written and oral.
            </p>

            <h2>15. Contact Information</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <p>
              Explanatum Limited<br />
              Email: legal@explanatum.co.uk<br />
              Address: 3 Heathmoor Road, Wirral, CH46 7UN<br />
            </p>
          </div>
        </div>
      </div>

      {/* CTA section */}
      <div className="bg-blue-600">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">Ready to get started?</span>
            <span className="block text-blue-200">Try our services today.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link
                to="/idea-generator"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 transition-all duration-200 ease-in-out"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
